import { useScoring, useGameInfo, useChips } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Trophy, 
  TrendingUp, 
  TrendingDown,
  Zap,
  Star,
  Award,
  AlertCircle,
  RefreshCw,
  Calculator,
  Target,
  Shield,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { FinancialTooltip } from '../components/FinancialTooltip';

const ScorecardPage = () => {
  const { myScore, seasonScore, weekInfo, topPerformers, loading, calculateScore, refresh } = useScoring();
  const { gameInfo } = useGameInfo();
  const { refresh: refreshChips } = useChips();
  const [calculating, setCalculating] = useState(false);
  const [scoringGuideOpen, setScoringGuideOpen] = useState(false);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const result = await calculateScore();
      
      // If a chip was used this week, show special notification and refresh chip status
      if (result?.active_chip) {
        toast.success(`Score calculated! Your ${result.active_chip.replace('_', ' ')} chip has been consumed.`, {
          duration: 5000
        });
        // Refresh chip status so ChipsPage shows updated counts
        await refreshChips();
      } else {
        toast.success('Score calculated successfully!');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-pulse text-emerald-400">Loading scorecard...</div>
      </div>
    );
  }

  const getBonusIcon = (bonus) => {
    switch (bonus) {
      case 'stability_low_drawdown':
      case 'flat_week':
        return <Shield className="h-3 w-3" />;
      case 'recovery_5pct':
      case 'recovery_10pct':
      case 'recovery_flat':
        return <TrendingUp className="h-3 w-3" />;
      case 'dividend':
      case 'etf_distribution':
      case 'staking_yield':
        return <Star className="h-3 w-3" />;
      default:
        return <Award className="h-3 w-3" />;
    }
  };

  const getBonusLabel = (bonus) => {
    const labels = {
      'stability_low_drawdown': 'Stability',
      'flat_week': 'Flat Week',
      'recovery_5pct': 'Recovery',
      'recovery_10pct': 'Big Recovery',
      'recovery_flat': 'Held Strong',
      'dividend': 'Dividend',
      'etf_distribution': 'Distribution',
      'staking_yield': 'Staking'
    };
    return labels[bonus] || bonus;
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-20" data-testid="scorecard-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
                <Calculator className="h-8 w-8 text-emerald-500" />
                Weekly Scorecard
              </h1>
              <p className="text-slate-400 mt-1">
                {gameInfo ? `Season ${gameInfo.current_season} - Week ${gameInfo.current_week}` : 'Loading...'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={refresh}
                className="border-slate-600 text-slate-300"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={handleCalculate}
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={calculating}
                data-testid="calculate-score-btn"
              >
                {calculating ? 'Calculating...' : 'Calculate Score'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Week Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Trophy className="h-8 w-8 text-amber-500/50" />
                <div>
                  <FinancialTooltip termKey="weekly_score">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Week Points</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-white" data-testid="week-points">
                    {myScore?.total_points || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Star className="h-8 w-8 text-emerald-500/50" />
                <div>
                  <FinancialTooltip termKey="season_total">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Season Total</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-white" data-testid="season-points">
                    {seasonScore?.total_points || myScore?.total_points || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Target className="h-8 w-8 text-indigo-500/50" />
                <div>
                  <FinancialTooltip termKey="holding_bonus">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Holding Bonus</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-emerald-400">
                    +{myScore?.holding_bonus || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-8 w-8 text-red-500/50" />
                <div>
                  <FinancialTooltip termKey="trade_penalty">
                    <p className="text-slate-400 text-sm underline decoration-dotted decoration-slate-600 cursor-help">Penalties</p>
                  </FinancialTooltip>
                  <p className="text-2xl font-bold text-red-400">
                    {myScore?.trade_penalty || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How Points Are Scored - Expandable Guide */}
        <Collapsible open={scoringGuideOpen} onOpenChange={setScoringGuideOpen} className="mb-8">
          <Card className="bg-slate-900 border-slate-800">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-slate-800/50 transition-colors rounded-t-lg">
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-indigo-400" />
                    How Points Are Scored
                  </span>
                  {scoringGuideOpen ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </CardTitle>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 space-y-6">
                {/* Base Performance Points */}
                <div>
                  <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Base Performance Points
                  </h4>
                  <p className="text-slate-400 text-sm mb-3">
                    Points are awarded based on the weekly price change of each asset in your portfolio.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-emerald-400 text-xs font-semibold mb-2">GAINS (Stocks/Commodities)</p>
                      <div className="space-y-1 text-xs text-slate-300">
                        <div className="flex justify-between"><span>+0.1% to +1%</span><span className="text-emerald-400">+1 pt</span></div>
                        <div className="flex justify-between"><span>+1% to +3%</span><span className="text-emerald-400">+2 pts</span></div>
                        <div className="flex justify-between"><span>+3% to +5%</span><span className="text-emerald-400">+4 pts</span></div>
                        <div className="flex justify-between"><span>+5% to +10%</span><span className="text-emerald-400">+7 pts</span></div>
                        <div className="flex justify-between"><span>+10% to +20%</span><span className="text-emerald-400">+12 pts</span></div>
                        <div className="flex justify-between"><span>+20%+</span><span className="text-emerald-400">+20 pts</span></div>
                      </div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3">
                      <p className="text-red-400 text-xs font-semibold mb-2">LOSSES (Stocks/Commodities)</p>
                      <div className="space-y-1 text-xs text-slate-300">
                        <div className="flex justify-between"><span>-0.1% to -1%</span><span className="text-slate-400">0 pts</span></div>
                        <div className="flex justify-between"><span>-1% to -3%</span><span className="text-red-400">-1 pt</span></div>
                        <div className="flex justify-between"><span>-3% to -5%</span><span className="text-red-400">-2 pts</span></div>
                        <div className="flex justify-between"><span>-5% to -10%</span><span className="text-red-400">-4 pts</span></div>
                        <div className="flex justify-between"><span>-10% to -20%</span><span className="text-red-400">-7 pts</span></div>
                        <div className="flex justify-between"><span>-20%+</span><span className="text-red-400">-12 pts</span></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs mt-2 italic">
                    Note: Crypto and ETFs have adjusted point scales with reduced penalties for losses.
                  </p>
                </div>

                {/* Bonuses Section */}
                <div>
                  <h4 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Bonus Points
                  </h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Dividend Bonus - Tiered by Yield */}
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="h-4 w-4 text-green-400" />
                        <span className="text-green-400 font-semibold text-sm">Dividends (by Yield)</span>
                      </div>
                      <p className="text-slate-300 text-xs mb-2">
                        Stock dividend points are based on the <span className="text-green-400">annual dividend yield</span>. Higher yield = more points!
                      </p>
                      <div className="space-y-1 text-xs">
                        <p className="text-slate-400 font-semibold text-xs mb-1">Stock Dividend Tiers:</p>
                        <div className="flex justify-between text-slate-300">
                          <span>0% - 1% yield</span>
                          <span className="text-green-400 font-semibold">+1 pt</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>1% - 2% yield</span>
                          <span className="text-green-400 font-semibold">+2 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>2% - 3% yield</span>
                          <span className="text-green-400 font-semibold">+3 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>3% - 5% yield</span>
                          <span className="text-green-400 font-semibold">+4 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>5%+ yield</span>
                          <span className="text-green-400 font-semibold">+5 pts</span>
                        </div>
                        <div className="border-t border-slate-700 mt-2 pt-2">
                          <div className="flex justify-between text-slate-300">
                            <span>ETF Distribution</span>
                            <span className="text-green-400 font-semibold">+2 pts</span>
                          </div>
                          <div className="flex justify-between text-slate-300">
                            <span>Crypto Staking</span>
                            <span className="text-green-400 font-semibold">+2 pts</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stability Bonus */}
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="h-4 w-4 text-blue-400" />
                        <span className="text-blue-400 font-semibold text-sm">Stability</span>
                      </div>
                      <p className="text-slate-300 text-xs mb-2">
                        Rewarded for steady performance with minimal volatility.
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-slate-300">
                          <span>Gain with &lt;2% drawdown</span>
                          <span className="text-blue-400 font-semibold">+3 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Flat week (±0.5%)</span>
                          <span className="text-blue-400 font-semibold">+1 pt</span>
                        </div>
                      </div>
                    </div>

                    {/* Recovery Bonus */}
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4 text-purple-400" />
                        <span className="text-purple-400 font-semibold text-sm">Diamond Hands</span>
                      </div>
                      <p className="text-slate-300 text-xs mb-2">
                        Bonus for holding through volatility and recovering.
                      </p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between text-slate-300">
                          <span>10%+ drop → positive</span>
                          <span className="text-purple-400 font-semibold">+8 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>5%+ drop → positive</span>
                          <span className="text-purple-400 font-semibold">+4 pts</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>5%+ drop → flat</span>
                          <span className="text-purple-400 font-semibold">+2 pts</span>
                        </div>
                      </div>
                    </div>

                    {/* Holding Bonus */}
                    <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4 text-indigo-400" />
                        <span className="text-indigo-400 font-semibold text-sm">Holding Bonus</span>
                      </div>
                      <p className="text-slate-300 text-xs">
                        Earn <span className="text-indigo-400 font-semibold">+1 point</span> for each active instrument in your portfolio (up to 10 pts).
                      </p>
                    </div>

                    {/* Power Play */}
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="h-4 w-4 text-amber-400" />
                        <span className="text-amber-400 font-semibold text-sm">Power Play</span>
                      </div>
                      <p className="text-slate-300 text-xs">
                        Designate one asset as your Power Play for the week. All points for that asset are multiplied by <span className="text-amber-400 font-semibold">3x</span>.
                      </p>
                    </div>

                    {/* Chips */}
                    <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Info className="h-4 w-4 text-cyan-400" />
                        <span className="text-cyan-400 font-semibold text-sm">Special Chips</span>
                      </div>
                      <p className="text-slate-300 text-xs">
                        Use chips like <span className="text-cyan-400">Wildcard</span> (unlimited trades) or <span className="text-cyan-400">Stop-Loss Shield</span> (protect worst performer) once per week.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Penalties Section */}
                <div>
                  <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Penalties
                  </h4>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <p className="text-red-400 text-xs font-semibold mb-1">Flash Crash</p>
                      <p className="text-slate-300 text-xs">Single-day drop &gt;15%</p>
                      <p className="text-red-400 font-semibold text-sm mt-1">-3 pts</p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <p className="text-red-400 text-xs font-semibold mb-1">No Diversity</p>
                      <p className="text-slate-300 text-xs">100% in one asset class</p>
                      <p className="text-red-400 font-semibold text-sm mt-1">-5 pts</p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                      <p className="text-red-400 text-xs font-semibold mb-1">Extra Trades</p>
                      <p className="text-slate-300 text-xs">More than 7 trades/week</p>
                      <p className="text-red-400 font-semibold text-sm mt-1">-10 pts each</p>
                    </div>
                  </div>
                </div>

                {/* Total Score Calculation */}
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                    <Calculator className="h-4 w-4 text-emerald-400" />
                    Total Score Formula
                  </h4>
                  <p className="text-slate-300 text-sm">
                    <span className="text-emerald-400 font-mono">Total Points</span> = 
                    <span className="text-blue-400"> (Base Performance × Multiplier)</span> + 
                    <span className="text-amber-400"> Bonuses</span> + 
                    <span className="text-indigo-400"> Holding Bonus</span> - 
                    <span className="text-red-400"> Penalties</span>
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Holdings Scorecard */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Holdings Performance</span>
                  <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                    {myScore?.holding_scores?.length || 0} instruments
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {myScore?.holding_scores?.length > 0 ? (
                  <div className="space-y-3">
                    {myScore.holding_scores.map((holding) => (
                      <div 
                        key={holding.symbol}
                        className={`p-4 rounded-lg border ${
                          holding.is_power_play 
                            ? 'bg-amber-500/10 border-amber-500/30' 
                            : 'bg-slate-800/50 border-slate-700/50'
                        }`}
                        data-testid={`score-${holding.symbol}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{holding.symbol}</span>
                            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                              {holding.asset_type}
                            </Badge>
                            {holding.is_power_play && (
                              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                                <Zap className="h-3 w-3 mr-1" />
                                3x
                              </Badge>
                            )}
                            {holding.dividend_bonus > 0 && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs">
                                <Award className="h-3 w-3 mr-1" />
                                Div +{holding.dividend_bonus}{holding.dividend_yield_pct > 0 ? ` (${holding.dividend_yield_pct}%)` : ''}
                              </Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`text-lg font-bold ${
                              holding.total_points >= 0 ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {holding.total_points >= 0 ? '+' : ''}{holding.total_points} pts
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`${
                              holding.price_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {holding.price_change_pct >= 0 ? (
                                <TrendingUp className="inline h-4 w-4 mr-1" />
                              ) : (
                                <TrendingDown className="inline h-4 w-4 mr-1" />
                              )}
                              {holding.price_change_pct >= 0 ? '+' : ''}{holding.price_change_pct}%
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            <span>Base: {holding.base_points}</span>
                            {holding.bonus_points > 0 && (
                              <span className="text-emerald-400">+{holding.bonus_points} bonus</span>
                            )}
                            {holding.multiplier > 1 && (
                              <span className="text-amber-400">×{holding.multiplier}</span>
                            )}
                          </div>
                        </div>

                        {/* Bonuses earned */}
                        {holding.bonuses_earned?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {holding.bonuses_earned.map((bonus, i) => (
                              <Badge 
                                key={i} 
                                className="bg-emerald-500/20 text-emerald-400 text-xs"
                              >
                                {getBonusIcon(bonus)}
                                <span className="ml-1">{getBonusLabel(bonus)}</span>
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Penalties */}
                        {holding.penalties_applied?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {holding.penalties_applied.map((penalty, i) => (
                              <Badge 
                                key={i} 
                                className="bg-red-500/20 text-red-400 text-xs"
                              >
                                <AlertCircle className="h-3 w-3 mr-1" />
                                {penalty}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calculator className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">No scores calculated yet</p>
                    <p className="text-slate-500 text-sm mt-1">Build your portfolio and click "Calculate Score"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Points Breakdown */}
            {myScore && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Points Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <FinancialTooltip termKey="base_performance">
                      <span className="text-slate-400 underline decoration-dotted decoration-slate-600 cursor-help">Base Performance</span>
                    </FinancialTooltip>
                    <span className="text-white">{myScore.total_base_points}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <FinancialTooltip termKey="bonuses">
                      <span className="text-slate-400 underline decoration-dotted decoration-slate-600 cursor-help">Bonuses</span>
                    </FinancialTooltip>
                    <span className="text-emerald-400">+{myScore.total_bonus_points}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <FinancialTooltip termKey="holding_bonus">
                      <span className="text-slate-400 underline decoration-dotted decoration-slate-600 cursor-help">Holding Bonus ({myScore.holding_scores?.length || 0} x 1)</span>
                    </FinancialTooltip>
                    <span className="text-emerald-400">+{myScore.holding_bonus}</span>
                  </div>
                  {myScore.trade_penalty !== 0 && (
                    <div className="flex justify-between text-sm">
                      <FinancialTooltip termKey="trade_penalty">
                        <span className="text-slate-400 underline decoration-dotted decoration-slate-600 cursor-help">Trade Penalties</span>
                      </FinancialTooltip>
                      <span className="text-red-400">{myScore.trade_penalty}</span>
                    </div>
                  )}
                  {myScore.diversity_penalty !== 0 && (
                    <div className="flex justify-between text-sm">
                      <FinancialTooltip termKey="diversity_penalty">
                        <span className="text-slate-400 underline decoration-dotted decoration-slate-600 cursor-help">Diversity Penalty</span>
                      </FinancialTooltip>
                      <span className="text-red-400">{myScore.diversity_penalty}</span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-slate-700">
                    <div className="flex justify-between">
                      <span className="text-white font-semibold">Total</span>
                      <span className="text-emerald-400 font-bold text-lg">{myScore.total_points} pts</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Performers */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                  <FinancialTooltip termKey="top_performers">
                    <span className="underline decoration-dotted decoration-slate-600 cursor-help">Top Performers</span>
                  </FinancialTooltip>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topPerformers.length > 0 ? (
                  <div className="space-y-3">
                    {topPerformers.slice(0, 5).map((performer, index) => (
                      <div key={performer.symbol} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                            index === 1 ? 'bg-slate-400/20 text-slate-300' :
                            index === 2 ? 'bg-amber-600/20 text-amber-500' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {index + 1}
                          </span>
                          <span className="text-white font-medium">{performer.symbol}</span>
                        </div>
                        <span className={`font-semibold ${
                          performer.change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {performer.change_pct >= 0 ? '+' : ''}{performer.change_pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">No data available</p>
                )}
              </CardContent>
            </Card>

            {/* Week Info */}
            {weekInfo && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg">This Week</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Players Scored</span>
                    <span className="text-white">{weekInfo.players_scored}</span>
                  </div>
                  {weekInfo.top_weekly_player && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Top Player</span>
                      <span className="text-amber-400">{weekInfo.top_weekly_player}</span>
                    </div>
                  )}
                  {weekInfo.top_weekly_score > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Top Score</span>
                      <span className="text-emerald-400">{weekInfo.top_weekly_score} pts</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScorecardPage;
